import { SocialSearchUserHit } from "src/app/model/social/social.types";

/**
 * Regras (puras) do autocomplete de usuários do feed social.
 *
 * O backend busca por FULLTEXT em modo booleano: cada palavra digitada (com 3+ caracteres) precisa
 * aparecer como PREFIXO de alguma palavra do nome ou do nickname ("Berengan" e "beren" acham
 * "Gabriel Berengan"). Essa busca é monótona: se o usuário só acrescenta letras, o novo resultado
 * é sempre um subconjunto do anterior. Quando o resultado anterior veio "completo" (menos itens
 * que o `take` pedido), ele já contém todos os candidatos — dá para refinar no cliente sem nova
 * consulta ao banco.
 */

/** Mínimo de caracteres para consultar o backend (espelha `SocialController.SearchUsers`). */
export const SOCIAL_SEARCH_MIN_CHARS = 3;

/** Quantos itens pedir por fonte (usuários / projetos) no dropdown de sugestões. */
export const SOCIAL_SEARCH_TAKE = 5;

/** Último resultado de usuários vindo do servidor, com a query que o gerou. */
export interface UserSearchSnapshot {
  query: string;
  hits: SocialSearchUserHit[];
  take: number;
}

/** Minúsculas e sem acentos — aproxima a collation *_ai_ci do MySQL ("José" casa com "jose"). */
function fold(value: string | null | undefined): string {
  return (value ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

/** Mesma tokenização do parser FULLTEXT do MySQL: letras, dígitos, `_` e `'` formam palavra. */
function tokens(value: string | null | undefined): string[] {
  return fold(value)
    .split(/[^\p{L}\p{N}_']+/u)
    .filter((t) => t.length > 0);
}

/** Palavras da query que o backend realmente usa (as curtas demais ele descarta). */
function queryTokens(query: string): string[] {
  return tokens(query).filter((t) => t.length >= SOCIAL_SEARCH_MIN_CHARS);
}

/**
 * True quando `query` estende a query do snapshot e o snapshot estava completo (veio com menos
 * itens que `take`) e não vazio — aí o filtro local é equivalente ao que o servidor devolveria.
 * Snapshot vazio não é reaproveitado: pode ter sido erro de rede, e reconsultar custa pouco.
 */
export function canRefineLocally(prev: UserSearchSnapshot | null, query: string): boolean {
  if (!prev || prev.hits.length === 0 || prev.hits.length >= prev.take) {
    return false;
  }
  const q = fold(query);
  const p = fold(prev.query);
  return p.length > 0 && q.length > p.length && q.startsWith(p);
}

/** Mesmo critério do backend: toda palavra da query é prefixo de alguma palavra do nome ou nickname. */
export function filterUserHitsByPrefix(hits: SocialSearchUserHit[], query: string): SocialSearchUserHit[] {
  const wanted = queryTokens(query);
  if (wanted.length === 0) {
    return [];
  }
  return hits.filter((h) => {
    const words = [...tokens(h.name), ...tokens(h.nickname)];
    return wanted.every((w) => words.some((word) => word.startsWith(w)));
  });
}
